import PropTypes from 'prop-types';

import styles from './SvgDetailPanel.module.scss';
import { types } from '../helpers';
import { format2DP, currencyFormatter } from 'utils/helpers';

const SvgDetailPanel = ({ id, x, y, data, height, width }) => {
    const { options } = data;

    const generateCorField = (field, value) => {
        return (
            <>
                <tspan x={x + 10} dy={14} className={styles.fieldTitle}>
                    {field}
                </tspan>
                <tspan className={styles.fieldText} dx={5}>
                    {value}
                </tspan>
            </>
        );
    };

    const generateValueField = (field, value) => {
        return (
            <>
                <tspan x={x + 10} dy={14} className={styles.fieldTitle}>
                    {field}
                </tspan>
                <tspan className={styles.fieldText} dx={5}>
                    {value}
                </tspan>
            </>
        );
    };

    const generateField = (field, rm, peer) => {
        return (
            <>
                <tspan x={x + 10} dy={14} className={styles.fieldTitle}>
                    {field}
                </tspan>

                <tspan className={styles.fieldTitle2} dx={5}>
                    RM
                </tspan>
                <tspan className={styles.fieldText} dx={2}>
                    {rm}
                </tspan>

                <tspan className={styles.vs} dx={5}>
                    vs
                </tspan>

                <tspan className={styles.fieldTitle2} dx={5}>
                    Peer
                </tspan>
                <tspan className={styles.fieldText} dx={2}>
                    {peer}
                </tspan>
            </>
        );
    };

    const generateOptions = () => {
        if (data.id === types.s1) {
            return (
                <>
                    {generateValueField(
                        'RM Revenue ($)',
                        currencyFormatter(options.totalRevenue)
                    )}
                    {generateValueField(
                        'Portfolio Unrealized Losses ($)',
                        currencyFormatter(options.negativeDelta)
                    )}
                </>
            );
        }

        if (data.id === types.s2) {
            return (
                <>
                    {generateField(
                        ' % CR Alerts',
                        `${format2DP(options.crAlertRm)}%`,
                        `${format2DP(options.crAlertPeer)}%`
                    )}
                    {generateField(
                        '% PRM Alerts',
                        `${format2DP(options.prmAlertRm)}%`,
                        `${format2DP(options.prmAlertPeer)}%`
                    )}
                </>
            );
        }

        if (data.id === types.s3) {
            return (
                <>
                    {generateField(
                        '% EO Trades',
                        `${format2DP(options.eoTradesRm)}%`,
                        `${format2DP(options.eoTradesPeer)}%`
                    )}
                </>
            );
        }

        if (data.id === types.s4) {
            return generateValueField(
                'Max Client : Product',
                `${options.maxClient}:1`
            );
        }

        if (data.id === types.cor) {
            return (
                <>
                    {!!options.corrS1S2 &&
                        generateCorField('S1-S2', options.corrS1S2)}
                    {!!options.corrS1S3 &&
                        generateCorField('S1-S3', options.corrS1S3)}
                    {!!options.corrS1S4 &&
                        generateCorField('S1-S4', options.corrS1S4)}
                    {!!options.corrS2S3 &&
                        generateCorField('S2-S3', options.corrS2S3)}
                    {!!options.corrS2S4 &&
                        generateCorField('S2-S4', options.corrS2S4)}
                    {!!options.corrS3S4 &&
                        generateCorField('S3-S4', options.corrS3S4)}
                </>
            );
        }
    };

    return (
        <g id={id} className={`${styles.detailPanel} d-panels`}>
            <rect
                id={`react-${data.name}`}
                x={x}
                y={y}
                height={height}
                width={width}
                className={styles.panel}
                rx={10}
                ry={10}
            />
            <text x={x + 10} y={y + 15}>
                <tspan className={styles.title}>{options.title}</tspan>

                {options.description && (
                    <tspan x={x + 10} dy={10} className={styles.fieldTitle}>
                        {options.description}
                    </tspan>
                )}

                {data.id !== types.cor && (
                    <>
                        <tspan x={x + 10} dy={14} className={styles.fieldTitle}>
                            Rank
                        </tspan>

                        <tspan className={styles.fieldText} dx={5}>
                            {options.rank}
                        </tspan>
                    </>
                )}

                <tspan x={x + 10} dy={14} className={styles.fieldTitle}>
                    Risk Score
                </tspan>
                <tspan className={styles.fieldText} dx={5}>
                    {data.score}
                </tspan>

                {generateOptions()}
            </text>
        </g>
    );
};

SvgDetailPanel.propTypes = {
    id: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    height: PropTypes.number,
    width: PropTypes.number,
    data: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        score: PropTypes.number,
        options: PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string,
            rank: PropTypes.string,
            corrS1S2: PropTypes.number,
            corrS1S3: PropTypes.number,
            corrS1S4: PropTypes.number,
            corrS2S3: PropTypes.number,
            corrS2S4: PropTypes.number,
            corrS3S4: PropTypes.number,
            rmRatio: PropTypes.number,
            peerRatio: PropTypes.number,
            crAlertRm: PropTypes.number,
            crAlertPeer: PropTypes.number,
            prmAlertRm: PropTypes.number,
            prmAlertPeer: PropTypes.number,
            eoTradesRm: PropTypes.number,
            eoTradesPeer: PropTypes.number,
            conceEoTradesRm: PropTypes.number,
            concEoTradesPeer: PropTypes.number,
            totalRevenue: PropTypes.number,
            negativeDelta: PropTypes.number,
            maxClient: PropTypes.number
        })
    }).isRequired
};

export default SvgDetailPanel;
